// QR Kiosk v2 — KV2-6 stream renderer (designer chat stage). User bubbles, bot
// bodies (markdown), replied-at lines, .cite / SR-citation chips, image-artifact
// thumbs, streaming block + caret, progress line, error line. (Bot-sent suggestion
// chips are deliberately NOT rendered here — see Chip below.)
// All bot text goes through react-markdown escaping / plain text — no
// dangerouslySetInnerHTML anywhere (§8). Tokens + glyphs only. §3-T4.
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ChatMessage } from "../../../shared/chatBot/models/chatBotModels";
import { isBotImageUrl } from "../../../shared/chatBot/streamBotMessage";
import { Machine } from "../../models/machinesModels";
import { Spark } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { MidasBotBody } from "./MidasBotBody";
import { MidasSurfaceContext } from "./midasSurfaceContext";
import { useBotImage } from "./useBotImage";
import { pageArtifacts, PageArtifactCard } from "./MidasPageArtifactCard";
import { MidasImageViewer } from "./MidasImageViewer";
import { getDraftForMessage, KioskDraftTicket } from "./draftTicketContract";
import { DraftTicketArtifactV2 } from "./DraftTicketArtifactV2";

const hhmm = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const botBodyStyle: React.CSSProperties = { maxWidth: "94%", fontSize: 15, lineHeight: 1.6, color: "var(--ink)" };
const repliedStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  color: "var(--faint)",
  marginTop: 8,
  letterSpacing: "0.04em",
};

export interface MidasMessagesProps {
  messages: ChatMessage[];
  streamingText: string;
  isLoading: boolean;
  currentStep: string | null;
  error: string | null;
  drafts: Record<string, KioskDraftTicket>;
  createdDrafts: Record<string, string>;
  machine: Machine;
  onOpenArtifact: (messageId: string) => void;
}

// §5b: bot document/page image thumbs open the fullscreen artifact takeover (zoom/
// pan/back), NOT the bare quick-look viewer. Route through the surface context with
// the ORIGINAL bot URL (the takeover re-fetches via useBotImage); the user's own
// uploaded photo keeps the MidasImageViewer quick-look below.
function BotImageThumb({ url, title }: { url: string; title?: string }) {
  const surface = useContext(MidasSurfaceContext);
  const blob = useBotImage(url);
  if (!blob) return null;
  return (
    <img
      src={blob}
      alt=""
      onClick={() => surface?.openImageArtifact(url, title)}
      style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 10, border: "1px solid var(--border)", cursor: "zoom-in" }}
    />
  );
}

// Citation chip (SR references). The bot-sent `suggestions` chips were REMOVED from
// the kiosk surface (owner, demo day 2026-08-04): the bot emits them in English while
// the kiosk UI runs Slovak, and a temporary PIN visitor has no use for them. The bot
// still sends msg.suggestions — we simply do not render them here. The desktop /midas
// surface keeps its own chips via shared/chatBot/components/ChatSuggestionChips.tsx.
function Chip({ label, onClick }: { label: string; onClick: () => void }) {
  const press = usePressed();
  return (
    <button type="button" className="cite" {...press.pressHandlers} onClick={onClick} style={{ border: "1px solid var(--midas-line)", background: "var(--midas-soft)", color: "var(--midas)", cursor: "pointer", ...press.pressStyle }}>
      {label}
    </button>
  );
}

export function MidasMessages(props: MidasMessagesProps) {
  const { messages, streamingText, isLoading, currentStep, error, drafts, createdDrafts, machine, onOpenArtifact } = props;
  const { translate } = useTranslation("public");
  const { translate: tChat } = useTranslation("chatBot");
  const navigate = useNavigate();
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);

  const imageArtifacts = (msg: ChatMessage) =>
    (msg.artifacts || []).filter((a) => (a.type === "image" || a.type === "user_image") && !!a.url && isBotImageUrl(a.url));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
      {messages.map((msg) => {
        if (msg.role === "user") {
          return (
            <div key={msg.id} style={{ alignSelf: "flex-end", maxWidth: "82%", display: "flex", flexDirection: "column", gap: 6 }}>
              {msg.imageUrl && (
                <img
                  src={msg.imageUrl}
                  alt={msg.imageName || ""}
                  onClick={() => setViewerSrc(msg.imageUrl!)}
                  style={{ width: 120, maxWidth: "100%", height: 120, objectFit: "cover", borderRadius: "13px 13px 4px 13px", border: "1px solid var(--user-bub-line)", cursor: "zoom-in", alignSelf: "flex-end" }}
                />
              )}
              {msg.text && (
                <div style={{ background: "var(--user-bub)", border: "1px solid var(--user-bub-line)", borderRadius: "13px 13px 4px 13px", padding: "9px 13px", fontSize: 13.5, lineHeight: 1.45, color: "var(--ink)" }}>
                  {msg.text}
                </div>
              )}
            </div>
          );
        }

        const draft = getDraftForMessage(msg, drafts); // null while flag off
        const isCreated = !!createdDrafts[msg.id];
        const artifacts = imageArtifacts(msg);
        const pageCards = pageArtifacts(msg);
        let repliedLine = translate("kioskV2MidasRepliedAt", { time: hhmm(msg.timestamp) });
        if (draft && !isCreated) repliedLine += ` · ${translate("kioskV2MidasDraftBadge")}`;
        else if (draft && isCreated) repliedLine += ` · ${translate("kioskV2MidasDraftCreatedLine")}`;

        return (
          <div key={msg.id} style={botBodyStyle}>
            <MidasBotBody text={msg.text} />

            {artifacts.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                {artifacts.map((a, i) => (
                  <BotImageThumb key={`${msg.id}-img-${i}`} url={a.url} title={a.title || undefined} />
                ))}
              </div>
            )}

            {/* Message-level page/schema artifacts (cross_reference / diagram_overlay) —
                tappable cards; tap → surface.openPageArtifact → MidasArtifactViewV2.
                Renders for BOTH live-stream and reopened-thread messages (both populate
                msg.artifacts). §8.4 / KV2_MIDAS_ARTIFACT_DIAGNOSIS. */}
            {pageCards.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                {pageCards.map((c) => (
                  <PageArtifactCard key={`${msg.id}-${c.key}`} card={c} />
                ))}
              </div>
            )}

            {msg.srCitations && msg.srCitations.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {msg.srCitations.map((c) => (
                  <Chip
                    key={c.reportNumber}
                    label={c.reportNumber}
                    onClick={() => navigate(`/public-v2/service-reports/${encodeURIComponent(c.reportNumber)}`)}
                  />
                ))}
              </div>
            )}

            {draft && (
              <DraftTicketArtifactV2
                card
                machine={machine}
                draft={draft}
                messageId={msg.id}
                created={isCreated}
                onOpen={() => onOpenArtifact(msg.id)}
              />
            )}

            <div style={repliedStyle}>{repliedLine}</div>
          </div>
        );
      })}

      {/* Streaming block — bot-body styles + trailing blink caret (reduced-motion safe via .kv2-caret). */}
      {streamingText && (
        <div style={botBodyStyle}>
          <MidasBotBody text={streamingText} streaming isLoading={isLoading} />
          <span className="kv2-caret" />
        </div>
      )}

      {/* Progress line — while loading. */}
      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)", letterSpacing: "0.05em" }}>
          <span style={{ color: "var(--midas)", display: "inline-flex" }}>
            <Spark size={11} />
          </span>
          {currentStep ? tChat(currentStep) : translate("kioskV2MidasThinking")}
        </div>
      )}

      {/* Error line — red-toned inline callout (no hex; from state, i18n'd). */}
      {error && !isLoading && (
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            padding: "10px 12px",
            borderRadius: 10,
            color: "color-mix(in oklab, var(--red) 82%, var(--ink))",
            background: "color-mix(in oklab, var(--red) 10%, var(--card))",
            border: "1px solid color-mix(in oklab, var(--red) 30%, var(--border))",
          }}
        >
          {error}
        </div>
      )}

      {viewerSrc && <MidasImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />}
    </div>
  );
}

export default MidasMessages;
